import api from "../api";
import {
  ContentResponse,
  ContentListResponse,
  ContentUploadResponse,
  ContentFormData,
  ContentFilter,
  ContentProgress,
  ContentAnalytics,
  ApiResponse,
} from "../../types/elearning";

class ContentService {
  private baseUrl = "/elearning/content";

  // Create new content
  async createContent(
    contentData: ContentFormData,
    courseId?: string,
    files?: File[]
  ): Promise<ContentResponse> {
    // Use course-specific endpoint if courseId is provided
    const endpoint = courseId
      ? `/elearning/courses/${courseId}/content`
      : `${this.baseUrl}`;

    let response;

    // Always use FormData for consistency with backend multer middleware
    const formData = new FormData();

    // Add content data fields directly
    formData.append("title", contentData.title || "");
    formData.append("description", contentData.description || "");
    formData.append(
      "type",
      contentData.contentType || contentData.type || "text"
    );
    formData.append(
      "content",
      typeof contentData.content === "string"
        ? contentData.content
        : contentData.content?.text || ""
    );
    formData.append(
      "estimatedDuration",
      contentData.estimatedDuration?.toString() || "10"
    );
    if (contentData.settings)
      formData.append("settings", JSON.stringify(contentData.settings));
    if (contentData.metadata)
      formData.append("metadata", JSON.stringify(contentData.metadata));
    if (contentData.tags)
      formData.append("tags", JSON.stringify(contentData.tags));
    if (contentData.order)
      formData.append("order", contentData.order.toString());
    if (contentData.visibility)
      formData.append("visibility", contentData.visibility);
    if (contentData.moduleId) formData.append("moduleId", contentData.moduleId);
    formData.append("mediaUrl", contentData.mediaUrl || "");
    formData.append("externalUrl", contentData.externalUrl || "");

    // Add new array fields for mixed content support
    if (contentData.mediaUrls && Array.isArray(contentData.mediaUrls)) {
      formData.append("mediaUrls", JSON.stringify(contentData.mediaUrls));
    }
    if (contentData.externalUrls && Array.isArray(contentData.externalUrls)) {
      formData.append("externalUrls", JSON.stringify(contentData.externalUrls));
    }
    if (contentData.contentItems && Array.isArray(contentData.contentItems)) {
      formData.append("contentItems", JSON.stringify(contentData.contentItems));
    }

    // Add files if provided
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append(`files`, file);
      });
    }

    response = await api.post<ContentResponse>(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response as unknown as ContentResponse;
  }

  // Get content list
  async getContentList(filters?: {
    courseId?: string;
    moduleId?: string;
    type?: string;
    status?: string;
    authorId?: string;
    page?: number;
    limit?: number;
  }): Promise<ContentListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    // Use course-specific endpoint if courseId is provided
    const endpoint = filters?.courseId
      ? `/elearning/courses/${filters.courseId}/content`
      : `${this.baseUrl}`;

    const response = await api.get<ContentListResponse>(
      `${endpoint}?${params.toString()}`
    );

    return response as unknown as ContentListResponse;
  }

  // Get content by ID
  async getContent(
    contentId: string,
    options?: { includeAnalytics?: boolean }
  ): Promise<ContentResponse> {
    const params = new URLSearchParams();
    if (options?.includeAnalytics) {
      params.append("includeAnalytics", "true");
    }

    const response = await api.get<ContentResponse>(
      `${this.baseUrl}/${contentId}?${params.toString()}`
    );

    return response as unknown as any;
  }

  // Get content by course
  async getContentByCourse(
    courseId: string,
    filters?: ContentFilter
  ): Promise<ContentListResponse> {
    const params = new URLSearchParams({
      courseId,
      ...this.buildFilterParams(filters || {}),
    });

    const response = await api.get<ContentListResponse>(
      `${this.baseUrl}/course/${courseId}?${params.toString()}`
    );

    return response as unknown as any;
  }

  // Get content by module
  async getContentByModule(
    moduleId: string,
    filters?: ContentFilter
  ): Promise<ContentListResponse> {
    const params = new URLSearchParams({
      moduleId,
      ...this.buildFilterParams(filters || {}),
    });

    const response = await api.get<ContentListResponse>(
      `${this.baseUrl}/module/${moduleId}?${params.toString()}`
    );

    return response as unknown as any;
  }

  // Update content
  async updateContent(
    contentId: string,
    updates: Partial<ContentFormData>,
    courseId?: string
  ): Promise<ContentResponse> {
    // Use course-specific endpoint if courseId is provided
    const endpoint = courseId
      ? `/elearning/courses/${courseId}/content/${contentId}`
      : `${this.baseUrl}/${contentId}`;

    const response = await api.put<ContentResponse>(endpoint, updates, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response as unknown as ContentResponse;
  }

  // Delete content
  async deleteContent(
    contentId: string,
    permanent = false,
    courseId?: string
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (permanent) {
      params.append("permanent", "true");
    }

    // Use course-specific endpoint if courseId is provided
    const endpoint = courseId
      ? `/elearning/courses/${courseId}/content/${contentId}`
      : `${this.baseUrl}/${contentId}`;

    const response = await api.delete<ApiResponse>(
      `${endpoint}?${params.toString()}`
    );

    return response as unknown as any;
  }

  // Duplicate content
  async duplicateContent(
    contentId: string,
    options?: {
      title?: string;
      targetCourseId?: string;
      targetModuleId?: string;
    }
  ): Promise<ContentResponse> {
    const response = await api.post<ContentResponse>(
      `${this.baseUrl}/${contentId}/duplicate`,
      options || {}
    );

    return response as unknown as any;
  }

  // Share content
  async shareContent(
    contentId: string,
    shareData: {
      shareWith: {
        userId: string;
        userType: "Teacher" | "Student" | "User";
      };
      permissions: "view" | "edit" | "admin";
      expiresAt?: Date;
      message?: string;
    }
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${contentId}/share`,
      shareData
    );

    return response as unknown as any;
  }

  // Search content
  async searchContent(
    searchTerm: string,
    filters?: ContentFilter
  ): Promise<ContentListResponse> {
    const params = new URLSearchParams({
      q: searchTerm,
      ...this.buildFilterParams(filters || {}),
    });

    const response = await api.get<ContentListResponse>(
      `${this.baseUrl}/search?${params.toString()}`
    );

    return response as unknown as any;
  }

  // Get content analytics
  async getContentAnalytics(
    contentId: string,
    period = "30d"
  ): Promise<ApiResponse<ContentAnalytics>> {
    const response = await api.get<ApiResponse<ContentAnalytics>>(
      `${this.baseUrl}/${contentId}/analytics?period=${period}`
    );

    return response as unknown as any;
  }

  // Progress tracking methods

  // Record content view
  async recordView(contentId: string): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${contentId}/view`
    );

    return response as unknown as any;
  }

  // Update content progress
  async updateProgress(
    contentId: string,
    progressData: {
      progress: number;
      timeSpent: number;
      completed?: boolean;
      metadata?: any;
    }
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${contentId}/progress`,
      progressData
    );

    return response as unknown as any;
  }

  // Get content progress for student
  async getContentProgress(
    contentId: string,
    studentId?: string
  ): Promise<ApiResponse<ContentProgress>> {
    const params = new URLSearchParams();
    if (studentId) {
      params.append("studentId", studentId);
    }

    const response = await api.get<ApiResponse<ContentProgress>>(
      `${this.baseUrl}/${contentId}/progress?${params.toString()}`
    );

    return response as unknown as any;
  }

  // Video-specific methods

  // Update video progress
  async updateVideoProgress(
    contentId: string,
    progressData: {
      currentTime: number;
      duration: number;
      watchedSegments: { start: number; end: number }[];
      playbackSpeed?: number;
      quality?: string;
    }
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${contentId}/video-progress`,
      progressData
    );

    return response as unknown as any;
  }

  // Get video analytics
  async getVideoAnalytics(contentId: string): Promise<ApiResponse> {
    const response = await api.get<ApiResponse>(
      `${this.baseUrl}/${contentId}/video-analytics`
    );

    return response as unknown as any;
  }

  // Content interaction methods

  // Add bookmark
  async addBookmark(
    contentId: string,
    bookmarkData: {
      title: string;
      description?: string;
      timestamp?: number;
      position?: number;
      tags?: string[];
      isPublic?: boolean;
    }
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${contentId}/bookmarks`,
      bookmarkData
    );

    return response as unknown as any;
  }

  // Get bookmarks
  async getBookmarks(contentId: string): Promise<ApiResponse> {
    const response = await api.get<ApiResponse>(
      `${this.baseUrl}/${contentId}/bookmarks`
    );

    return response as unknown as any;
  }

  // Add comment
  async addComment(
    contentId: string,
    commentData: {
      content: string;
      timestamp?: number;
      parentId?: string;
    }
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${contentId}/comments`,
      commentData
    );

    return response as unknown as any;
  }

  // Get comments
  async getComments(
    contentId: string,
    options?: { page?: number; limit?: number }
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());

    const response = await api.get<ApiResponse>(
      `${this.baseUrl}/${contentId}/comments?${params.toString()}`
    );

    return response as unknown as any;
  }

  // File upload methods

  // Upload files to content
  async uploadFiles(
    contentId: string,
    files: File[],
    onProgress?: (progress: number) => void
  ): Promise<ContentUploadResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await api.post<ContentUploadResponse>(
      `${this.baseUrl}/${contentId}/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return response as unknown as any;
  }

  // Delete file from content
  async deleteFile(contentId: string, fileUrl: string): Promise<ApiResponse> {
    const response = await api.delete<ApiResponse>(
      `${this.baseUrl}/${contentId}/files`,
      { data: { fileUrl } }
    );

    return response as unknown as any;
  }

  // Utility method to build filter parameters
  private buildFilterParams(filters: ContentFilter): Record<string, string> {
    const params: Record<string, string> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params[key] = value.join(",");
        } else if (value instanceof Date) {
          params[key] = value.toISOString();
        } else {
          params[key] = value.toString();
        }
      }
    });

    return params;
  }

  // Batch operations

  // Bulk delete content
  async bulkDeleteContent(
    contentIds: string[],
    permanent = false
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/bulk-delete`,
      { contentIds, permanent }
    );

    return response as unknown as any;
  }

  // Bulk update content
  async bulkUpdateContent(
    updates: { contentId: string; updates: Partial<ContentFormData> }[]
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/bulk-update`,
      { updates }
    );

    return response as unknown as any;
  }

  // Export content
  async exportContent(
    contentIds: string[],
    format: "json" | "pdf" | "scorm"
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/export`,
      { contentIds, format },
      { responseType: "blob" }
    );

    return response as unknown as any;
  }

  // Import content
  async importContent(
    file: File,
    courseId: string,
    moduleId?: string
  ): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("courseId", courseId);
    if (moduleId) formData.append("moduleId", moduleId);

    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response as unknown as any;
  }
}

export const contentService = new ContentService();
export default contentService;
